import { useEffect, useState } from "react";
import PageLoader from "../../../components/pageLoader";
import Toast from "../../../components/toast";
import ToastContainer from "../../../components/toastContainer";
import {
  CALENDAR_STATUS,
  CALENDAR_STATUS_PRIORITY,
} from "../../../constants/index";
import {
  getArtistCalendar,
  getArtistsCalendarData,
} from "../../../api/artistApi";

const DAYS = ["S", "M", "T", "W", "T", "F", "S"];

const generateMonthDays = (year, month) => {
  const days = [];
  const lastDay = new Date(year, month, 0).getDate();

  for (let i = 1; i <= lastDay; i++) {
    const day = String(i).padStart(2, "0");
    days.push(`${year}-${String(month).padStart(2, "0")}-${day}`);
  }

  return days;
};

const Info = ({ label, value }) => (
  <div>
    <span className="text-slate-400">{label}: </span>
    <span className="text-white">{value || "-"}</span>
  </div>
);

const CalendarTab = ({ artist }) => {
  const today = new Date();

  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [calendarData, setCalendarData] = useState({});
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);

  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const getDayStatus = (bookings = []) => {
    if (!bookings.length) return null;

    return bookings
      .map((b) => b.status)
      .sort(
        (a, b) =>
          CALENDAR_STATUS_PRIORITY.indexOf(a) -
          CALENDAR_STATUS_PRIORITY.indexOf(b),
      )[0];
  };

  const fetchCalendar = async () => {
    try {
      setLoading(true);

      const data = await getArtistCalendar(artist.id, year, month);

      const mapped = {};

      (data.bookings || []).forEach((booking) => {
        if (!mapped[booking.date]) {
          mapped[booking.date] = [];
        }
        mapped[booking.date].push(booking);
      });

      setCalendarData(mapped);
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail || "Failed to load calendar",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendar();
  }, [year, month]);

  if (loading) return <PageLoader />;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Calendar */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setMonth((m) => (m === 1 ? 12 : m - 1))}
            className="text-slate-400 hover:text-white"
          >
            ‹
          </button>

          <h2 className="text-white font-medium">
            {new Date(year, month - 1).toLocaleString("default", {
              month: "long",
              year: "numeric",
            })}
          </h2>

          <button
            onClick={() => setMonth((m) => (m === 12 ? 1 : m + 1))}
            className="text-slate-400 hover:text-white"
          >
            ›
          </button>
        </div>

        {/* Days */}
        <div className="grid grid-cols-7 gap-2 text-center text-xs text-slate-400 mb-2">
          {DAYS.map((d) => (
            <div key={d}>{d}</div>
          ))}
        </div>

        {/* Dates */}
        <div className="grid grid-cols-7 gap-2">
          {generateMonthDays(year, month).map((date) => {
            const bookings = calendarData[date] || [];
            const dayStatus = getDayStatus(bookings);
            const statusClass = dayStatus ? CALENDAR_STATUS[dayStatus] : null;

            return (
              <button
                key={date}
                onClick={() => {
                  setSelectedDate(date);
                  setSelectedBookings(calendarData[date] || []);
                }}
                className={`h-10 rounded text-sm flex items-center justify-center
                  ${
                    statusClass
                      ? `${statusClass} text-white`
                      : "text-slate-300 hover:bg-slate-800"
                  }`}
              >
                {date.split("-")[2]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Details */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl p-4">
        {selectedDate ? (
          selectedBookings.length ? (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-white">
                Bookings on {selectedDate}
              </h3>

              {selectedBookings.map((booking) => (
                <div
                  key={booking.id}
                  className="border border-white/10 rounded-lg p-3 text-sm"
                >
                  <Info label="Date" value={booking.date} />
                  <Info label="Booking Status" value={booking.status} />
                  <Info
                    label="Payment Status"
                    value={booking.payment_status}
                  />
                  <Info
                    label="Promoter"
                    value={booking.promoter?.company_name}
                  />
                  <Info
                    label="Contact Info"
                    value={`${booking.promoter?.contact_name || ""} ${
                      booking.promoter?.contact_phone || ""
                    }`}
                  />
                  <Info
                    label="Location"
                    value={`${booking.address?.address_1 || ""}, ${
                      booking.address?.city || ""
                    }`}
                  />
                  <Info label="Set Time" value={booking.set_time} />
                  <Info
                    label="Closest Airports"
                    value={booking.closest_airports
                      ?.map((a) => a.name)
                      .join(", ")}
                  />
                  <Info label="Agreed Fee" value={booking.agreed_fee} />
                  <Info label="Booking Fee" value={booking.booking_fee} />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-400 text-sm">No bookings for this date</p>
          )
        ) : (
          <p className="text-slate-400 text-sm">
            Select a date to view booking details
          </p>
        )}

        {/* {selectedDate ? (
          <div className="space-y-2 text-sm">
            <Info label="Date" value={selectedDate.date} />
            <Info label="Booking Status" value={selectedDate.status} />
            <Info label="Payment Status" value={selectedDate.payment_status} />
            <Info
              label="Promoter"
              value={selectedDate.promoter?.company_name}
            />
            <Info
              label="Contact Info"
              value={`${selectedDate.promoter?.contact_name || ""} ${
                selectedDate.promoter?.contact_phone || ""
              }`}
            />
            <Info
              label="Location"
              value={`${selectedDate.address?.address_1 || ""}, ${
                selectedDate.address?.city || ""
              }`}
            />
            <Info label="Set Time" value={selectedDate.set_time} />
            <Info
              label="Closest Airports"
              value={selectedDate.closest_airports
                ?.map((a) => a.name)
                .join(", ")}
            />
            <Info label="Agreed Fee" value={selectedDate.agreed_fee} />
            <Info label="Booking Fee" value={selectedDate.booking_fee} />
          </div>
        ) : (
          <p className="text-slate-400 text-sm">
            Select a date to view booking details
          </p>
        )} */}
      </div>

      <ToastContainer>
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </ToastContainer>
    </div>
  );
};

export default CalendarTab;
